import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/supabase/client";
import { useAuth } from "@/lib/auth-context";
import {
  createIllustrationJob,
  getJobStatus,
  generateText as sbGenerateText,
  renderPdf as sbRenderPdf,
} from "@/lib/storybooksApi";
import { useStorybookContext, StorybookClassroom } from "./useStorybookContext";
import type { Database } from "@/types/database";

/**
 * StorybooksCreate
 * - Loads the book by :bookId
 * - Lets teacher add notes
 * - Uses the Render storybooks service for illustration jobs and polling
 */

type StoryBookRow = Database["public"]["Tables"]["story_books"]["Row"];
type StoryPageRow = Database["public"]["Tables"]["story_pages"]["Row"];

interface StorybooksCreateProps {
  book?: StoryBookRow | null;
  classroom_id?: string;
}

interface PageState {
  id: string | null;
  page_no: number;
  text: string;
  image_url: string;
  illustration_ready: boolean;
  last_error: string | null;
  version: number;
  image_updated_at: string | null;
  jobStatus: string;
}

interface ToastState {
  kind: "ok" | "err";
  msg: string;
}

interface JobStatus {
  job_id?: string;
  status?: string;
  completed_pages?: number;
  total_pages?: number;
  progress?: JobProgressEntry[] | { pages?: JobProgressEntry[] } | null;
}

interface JobProgressEntry {
  page_no?: number;
  status?: string;
  key?: string;
  error?: string;
}

interface PageRowInput {
  id?: string | null;
  page_no?: number | null;
  text?: string | null;
  illustration_ready?: boolean | string | null;
  image_url?: string | null;
  last_error?: string | null;
  version?: number | string | null;
  image_updated_at?: string | null;
}

const JOB_POLL_INTERVAL = 3000;

type PageStatusKey = "ready" | "error" | "running" | "queued" | "pending" | "complete";

const PAGE_STATUS_LABELS: Record<PageStatusKey, string> = {
  ready: "Ready",
  error: "Needs attention",
  running: "Rendering",
  queued: "Queued",
  pending: "Pending",
  complete: "Complete",
};

const PAGE_STATUS_CLASSES: Record<PageStatusKey, string> = {
  ready: "bg-emerald-100 text-emerald-800",
  error: "bg-rose-100 text-rose-800",
  running: "bg-blue-100 text-blue-800",
  queued: "bg-amber-100 text-amber-800",
  pending: "bg-neutral-100 text-neutral-600",
  complete: "bg-emerald-100 text-emerald-800",
};

const JOB_DONE_STATUSES = new Set(["complete", "complete-with-errors", "failed", "error"]);

export default function StorybooksCreate(props: StorybooksCreateProps) {
  const navigate = useNavigate();
  const { bookId: routeBookId } = useParams();
  const { session } = useAuth();
  const { classroom, enabled, loading: contextLoading, error: contextError, refresh } =
    useStorybookContext() as {
      classroom: StorybookClassroom | null;
      enabled: boolean;
      loading: boolean;
      error: Error | null;
      refresh: () => Promise<void>;
    };

  // If parent passed a book in props, prefer that, otherwise use route param
  const incomingBook = props.book || null;
  const [book, setBook] = useState<StoryBookRow | null>(incomingBook);
  const [loading, setLoading] = useState(!incomingBook);
  const [teacherNotes, setTeacherNotes] = useState("");
  const [narrative, setNarrative] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [toast, setToast] = useState<ToastState | null>(null);
  const [busy, setBusy] = useState(false);
  const [autoCreating, setAutoCreating] = useState(false);
  const [pages, setPages] = useState<PageState[]>([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pagesDirty, setPagesDirty] = useState(false);
  const [savingPages, setSavingPages] = useState(false);
  const [removedPageIds, setRemovedPageIds] = useState<string[]>([]);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);

  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const bookId = useMemo(
    () => (incomingBook?.id || routeBookId || "").trim(),
    [incomingBook, routeBookId]
  );

  const teacherId = session?.user?.id || null;

  function showOk(msg: string) {
    setToast({ kind: "ok", msg });
    setTimeout(() => setToast(null), 4000);
  }
  function showErr(msg: string) {
    setToast({ kind: "err", msg });
    setTimeout(() => setToast(null), 6000);
  }

  const splitNarrativeIntoPages = useCallback((text: string): string[] => {
    const raw = (text || "")
      .split(/\n{2,}/)
      .map((block) => block.trim())
      .filter(Boolean);
    if (raw.length) return raw;
    const single = (text || "").trim();
    return single ? [single] : [];
  }, []);

  const toPageState = useCallback((row: PageRowInput, index = 0): PageState => {
    const pageNo = Number(row?.page_no ?? index + 1) || index + 1;
    const ready = Boolean(row?.illustration_ready);
    const lastError = row?.last_error || null;
    const status = ready ? "ready" : lastError ? "error" : "pending";
    return {
      id: row?.id || null,
      page_no: pageNo,
      text: row?.text || "",
      image_url: row?.image_url || "",
      illustration_ready: ready,
      last_error: lastError,
      version: Number(row?.version ?? 0) || 0,
      image_updated_at: row?.image_updated_at || null,
      jobStatus: status,
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const handleJobProgress = useCallback((payload: JobStatus) => {
    if (!payload) return;
    setJobStatus(payload);
    setPollError(null);
    const progressObj = payload?.progress as { pages?: JobProgressEntry[] } | JobProgressEntry[] | null;
    const progressPages: JobProgressEntry[] = Array.isArray((progressObj as { pages?: JobProgressEntry[] })?.pages)
      ? (progressObj as { pages: JobProgressEntry[] }).pages
      : Array.isArray(progressObj)
        ? progressObj
        : [];
    if (!progressPages.length) return;
    setPages((prev) =>
      prev.map((page) => {
        const update = progressPages.find(
          (entry) => Number(entry?.page_no) === Number(page.page_no)
        );
        if (!update) return page;
        const status = update.status || page.jobStatus;
        const next = { ...page, jobStatus: status };
        if (update.key && !page.illustration_ready) {
          next.image_url = update.key;
        }
        if (status === "error" && update.error) {
          next.last_error = update.error;
        }
        return next;
      })
    );
  }, []);

  const loadPages = useCallback(async () => {
    if (!bookId) return;
    setPagesLoading(true);
    try {
      const { data, error } = await supabase
        .from("story_pages")
        .select(
          "id, page_no, text, illustration_ready, image_url, last_error, version, image_updated_at"
        )
        .eq("book_id", bookId)
        .order("page_no", { ascending: true });
      if (error) throw error;
      const rows = Array.isArray(data) ? data : [];
      setPages(rows.map((row, index) => toPageState(row, index)));
      setPagesDirty(false);
      setRemovedPageIds([]);
    } catch (err) {
      showErr((err as Error).message || "Unable to load pages.");
    } finally {
      setPagesLoading(false);
    }
  }, [bookId, toPageState]);

  const persistPages = useCallback(
    async ({ silent = false } = {}) => {
      if (!bookId) return false;
      if (!pages.length && !removedPageIds.length) return false;
      setSavingPages(true);
      try {
        if (!pages.length && removedPageIds.length) {
          const { error: delErr } = await supabase
            .from("story_pages")
            .delete()
            .in("id", removedPageIds);
          if (delErr) throw delErr;
          setRemovedPageIds([]);
          setPagesDirty(false);
          if (!silent) showOk("Pages updated.");
          return true;
        }

        const payload = pages.map((page, index) => ({
          ...(page.id ? { id: page.id } : {}),
          book_id: bookId,
          page_no: index + 1,
          text: (page.text || "").trim(),
        }));

        const { data, error } = await supabase
          .from("story_pages")
          .upsert(payload, { onConflict: "book_id,page_no" })
          .select(
            "id, page_no, text, illustration_ready, image_url, last_error, version, image_updated_at"
          );
        if (error) throw error;

        if (removedPageIds.length) {
          const { error: delErr } = await supabase
            .from("story_pages")
            .delete()
            .in("id", removedPageIds);
          if (delErr) throw delErr;
          setRemovedPageIds([]);
        }

        const rows = Array.isArray(data) ? data : [];
        setPages(rows.map((row, index) => toPageState(row, index)));
        setPagesDirty(false);
        if (!silent) showOk("Pages saved.");
        return true;
      } catch (err) {
        if (!silent) showErr((err as Error).message || "Failed to save pages.");
        throw err;
      } finally {
        setSavingPages(false);
      }
    },
    [bookId, pages, removedPageIds, toPageState]
  );

  const startJobPolling = useCallback(
    (jobId: string) => {
      if (!jobId) return;
      setPollError(null);

      const poll = async () => {
        try {
          const result = await getJobStatus({ job_id: jobId });
          if (!result.data.job) return;
          const job = result.data.job;
          const progressData = {
            job_id: job.id,
            status: job.status,
            completed_pages: job.completed_pages,
            total_pages: job.total_pages,
            progress: job.progress?.pages || null,
          };
          handleJobProgress(progressData);
          if (JOB_DONE_STATUSES.has(job.status)) {
            stopPolling();
            await loadPages();
            if (typeof refresh === "function") refresh();
          }
        } catch (err) {
          setPollError((err as Error).message || "Unable to poll job status.");
        }
      };

      poll();
      if (pollTimer.current) clearInterval(pollTimer.current);
      pollTimer.current = setInterval(poll, JOB_POLL_INTERVAL);
    },
    [handleJobProgress, loadPages, refresh, stopPolling]
  );

  const syncPagesFromNarrative = useCallback(() => {
    if (!narrative) {
      showErr("Generate a narrative first.");
      return;
    }
    const blocks = splitNarrativeIntoPages(narrative);
    if (!blocks.length) {
      showErr("Narrative is empty.");
      return;
    }
    setPages((prev) => {
      const next: PageState[] = blocks.map((text, index) => {
        const existing: Partial<PageState> = prev[index] || {};
        return {
          id: existing.id || null,
          page_no: index + 1,
          text,
          illustration_ready: Boolean(existing.illustration_ready && existing.image_url),
          image_url: existing.image_url || "",
          last_error: null,
          version: existing.version || 0,
          image_updated_at: existing.image_updated_at || null,
          jobStatus: existing.jobStatus || (existing.illustration_ready ? "ready" : "pending"),
        };
      });
      if (prev.length > blocks.length) {
        const removed = prev
          .slice(blocks.length)
          .map((page) => page?.id)
          .filter((id): id is string => id !== null);
        if (removed.length) {
          setRemovedPageIds((ids) => Array.from(new Set([...ids, ...removed])));
        }
      }
      setPagesDirty(true);
      return next;
    });
  }, [narrative, splitNarrativeIntoPages]);

  const addPage = useCallback(() => {
    setPages((prev) => {
      const nextPageNo = prev.length + 1;
      const next: PageState[] = [
        ...prev,
        {
          id: null,
          page_no: nextPageNo,
          text: "",
          illustration_ready: false,
          image_url: "",
          last_error: null,
          version: 0,
          image_updated_at: null,
          jobStatus: "pending",
        },
      ];
      setPagesDirty(true);
      return next;
    });
  }, []);

  const removePage = useCallback((index: number) => {
    setPages((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const target = prev[index];
      if (target?.id) {
        const targetId = target.id;
        setRemovedPageIds((ids) => Array.from(new Set([...ids, targetId])));
      }
      const next = prev
        .filter((_, idx) => idx !== index)
        .map((page, idx) => ({ ...page, page_no: idx + 1 }));
      setPagesDirty(true);
      return next;
    });
  }, []);

  const updatePageText = useCallback((index: number, value: string) => {
    setPages((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const next = [...prev];
      next[index] = { ...next[index], text: value };
      setPagesDirty(true);
      return next;
    });
  }, []);

  const onSavePages = useCallback(async () => {
    try {
      await persistPages({ silent: false });
    } catch (err) {
      // toast handled in persistPages
    }
  }, [persistPages]);

  // Load book details (title, narrative, classroom_id, pdf_url)
  useEffect(() => {
    let ignore = false;
    async function run() {
      if (!bookId || incomingBook) return;
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("story_books")
          .select(
            "id, classroom_id, title, grade_level, teacher_notes, narrative, pdf_url, include_illustrations, status, updated_at, created_at, settings",
          )
          .eq("id", bookId)
          .maybeSingle();

        if (error && error.code !== "PGRST116") {
          throw error;
        }

        if (!ignore) {
          if (!data) {
            throw new Error("Storybook not found");
          }
          setBook(data as StoryBookRow);
          setNarrative(data.narrative || "");
          setPdfUrl(data.pdf_url || "");
          setTeacherNotes(data.teacher_notes || "");
        }
      } catch (err) {
        if (!ignore) {
          setToast({ kind: "err", msg: (err as Error).message || "Unable to load storybook." });
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => {
      ignore = true;
    };
  }, [bookId, incomingBook]);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  // Auto-create a draft when navigating directly to /storybooks/new
  useEffect(() => {
    if (incomingBook) return;
    if (bookId) return;
    if (!enabled || contextLoading) return;
    if (contextError) return;
    if (!classroom?.id || !teacherId) return;

    let ignore = false;
    const createDraft = async () => {
      setAutoCreating(true);
      try {
        const defaultTitle = classroom?.name ? `${classroom.name} Storybook` : "Classroom Story";
        const { data, error } = await supabase
          .from("story_books")
          .insert({
            classroom_id: classroom.id,
            teacher_id: teacherId,
            title: defaultTitle,
            grade_level: classroom?.grade_level || null,
          })
          .select("id")
          .single();
        if (error) throw error;
        if (!ignore && data?.id) {
          navigate(`/portal/teacher/storybooks/${data.id}`, { replace: true });
        }
      } catch (err) {
        if (!ignore) {
          setToast({ kind: "err", msg: (err as Error).message || "Unable to start a new storybook." });
        }
      } finally {
        if (!ignore) {
          setAutoCreating(false);
        }
      }
    };

    createDraft();
    return () => {
      ignore = true;
    };
  }, [
    bookId,
    classroom?.grade_level,
    classroom?.id,
    classroom?.name,
    contextError,
    contextLoading,
    enabled,
    incomingBook,
    navigate,
    teacherId,
  ]);

  const classroomId = book?.classroom_id || props.classroom_id || "";

  useEffect(() => {
    if (!narrative) return;
    setPages((prev) => {
      if (prev.length) return prev;
      const blocks = splitNarrativeIntoPages(narrative);
      if (!blocks.length) return prev;
      setPagesDirty(true);
      return blocks.map((text, index) =>
        toPageState({ page_no: index + 1, text, illustration_ready: false, image_url: "" }, index)
      );
    });
  }, [narrative, splitNarrativeIntoPages, toPageState]);

  useEffect(() => stopPolling, [stopPolling]);

  useEffect(() => {
    stopPolling();
    setJobStatus(null);
  }, [bookId, stopPolling]);

  async function onGenerateText() {
    if (!bookId) return showErr("Missing book id.");
    setBusy(true);
    try {
      const out = await sbGenerateText({
        book_id: bookId,
        teacher_notes: teacherNotes,
      }) as { book?: Partial<StoryBookRow> } | null;
      const nextNarrative = out?.book?.narrative || "";
      setNarrative(nextNarrative);
      if (out?.book) {
        setBook((prev) => ({ ...(prev || {} as StoryBookRow), ...out.book } as StoryBookRow));
        await loadPages();
      }
      await refresh?.();
      showOk("Narrative generated.");
    } catch (err) {
      showErr((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // This illustrates calling the illustrations endpoint with a simple 1-page payload.
  // If you have a richer page editor, map your pages into [{page_no, text}, ...].
  async function onGenerateIllustrations() {
    if (!bookId) return showErr("Missing book id.");
    if (!classroomId) return showErr("Missing classroom id.");
    if (!pages.length) return showErr("Add at least one storyboard page.");
    if (pages.some((page) => !(page.text || "").trim())) {
      return showErr("Each page needs a short description.");
    }
    // V2 workflow: Allow illustrations to be generated if pages have text,
    // even without a full narrative. The narrative can be generated after illustrations.

    setBusy(true);
    try {
      await persistPages({ silent: true });
      const payloadPages = pages.map((page) => ({ page_no: page.page_no, text: page.text }));

      const response = await createIllustrationJob({
        book_id: bookId,
        classroom_id: classroomId,
        pages: payloadPages,
      });
      const job = response.data.job;
      const result = {
        job_id: job.id,
        status: job.status,
        completed_pages: job.completed_pages,
        progress: job.progress?.pages || null,
      };

      if (result.job_id) {
        const initial: JobStatus = {
          job_id: result.job_id,
          status: result.status || "queued",
          completed_pages: Number(result.completed_pages ?? 0) || 0,
          total_pages: payloadPages.length,
          progress: result.progress || null,
        };
        handleJobProgress(initial);
        startJobPolling(result.job_id);
        showOk(
          JOB_DONE_STATUSES.has(initial.status || "")
            ? "Illustrations generated."
            : "Illustrations job started."
        );
        if (JOB_DONE_STATUSES.has(initial.status || "")) {
          await loadPages();
        }
      } else {
        showOk("Illustrations request sent (check page previews).");
        await loadPages();
      }
      await refresh?.();
    } catch (err) {
      showErr((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function onExportPdf() {
    if (!bookId) return showErr("Missing book id.");
    setBusy(true);
    try {
      const out = await sbRenderPdf({
        book_id: bookId,
      }) as { url?: string; signedUrl?: string; pdf_url?: string } | null;
      const nextUrl = out?.url || out?.signedUrl || out?.pdf_url || "";
      if (nextUrl) {
        setPdfUrl(nextUrl);
        showOk("PDF ready.");
      } else {
        showErr("Export succeeded but no pdf_url returned.");
      }
    } catch (err) {
      showErr((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!bookId) {
    return (
      <div className="space-y-4 p-6">
        {toast && (
          <div
            className={`rounded px-4 py-3 text-sm ${
              toast.kind === "ok" ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"
            }`}
          >
            {toast.msg}
          </div>
        )}
        <p className="text-sm text-neutral-600">
          {autoCreating
            ? "Starting your new storybook…"
            : "No book selected. Return to Storybooks."}
        </p>
        <button className="btn" onClick={() => navigate("/portal/teacher/storybooks")}>
          Back to Storybooks
        </button>
      </div>
    );
  }

  // Calculate workflow progress for visual indicator
  const pagesReady = pages.length > 0 && pages.every((p) => (p.text || "").trim());
  const illustrationsStarted = pages.some((p) => p.illustration_ready || p.jobStatus === "running" || p.jobStatus === "queued");
  const illustrationsComplete = pages.length > 0 && pages.every((p) => p.illustration_ready);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">{book?.title || "New Storybook"}</h1>
        <p className="text-sm text-neutral-600">
          Create beautiful illustrated storybooks for your classroom.
        </p>
      </header>

      {toast && (
        <div
          className={`mb-4 rounded px-4 py-3 text-sm ${
            toast.kind === "ok" ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"
          }`}
        >
          {toast.msg}
        </div>
      )}

      {/* Workflow Guide */}
      <section className="mb-8 rounded-xl border border-amber-200 bg-amber-50 p-4">
        <h2 className="font-semibold text-amber-900 mb-3">How it works</h2>
        <div className="grid gap-3 md:grid-cols-3">
          <div className={`flex items-start gap-3 rounded-lg p-3 ${pagesReady ? "bg-emerald-100" : "bg-white"}`}>
            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${pagesReady ? "bg-emerald-600 text-white" : "bg-amber-600 text-white"}`}>1</span>
            <div>
              <p className="font-medium text-sm">Describe your scenes</p>
              <p className="text-xs text-neutral-600">Write what you want to see on each page</p>
            </div>
          </div>
          <div className={`flex items-start gap-3 rounded-lg p-3 ${illustrationsComplete ? "bg-emerald-100" : illustrationsStarted ? "bg-blue-100" : "bg-white"}`}>
            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${illustrationsComplete ? "bg-emerald-600 text-white" : illustrationsStarted ? "bg-blue-600 text-white" : "bg-neutral-300 text-neutral-600"}`}>2</span>
            <div>
              <p className="font-medium text-sm">Generate illustrations</p>
              <p className="text-xs text-neutral-600">AI creates consistent characters across pages</p>
            </div>
          </div>
          <div className={`flex items-start gap-3 rounded-lg p-3 ${pdfUrl ? "bg-emerald-100" : "bg-white"}`}>
            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${pdfUrl ? "bg-emerald-600 text-white" : "bg-neutral-300 text-neutral-600"}`}>3</span>
            <div>
              <p className="font-medium text-sm">Export & share</p>
              <p className="text-xs text-neutral-600">Download as PDF or share digitally</p>
            </div>
          </div>
        </div>
      </section>

      {loading ? (
        <div className="text-sm text-neutral-500">Loading book…</div>
      ) : (
        <>
          {/* Step 1: Storyboard pages - PRIMARY */}
          <section className="mb-8">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <div>
                <h3 className="text-lg font-semibold">Step 1: Describe Your Scenes</h3>
                <p className="text-sm text-neutral-600">
                  Tell us what you want to see on each page. Be descriptive about characters, settings, and emotions.
                </p>
              </div>
              <button
                className="px-4 py-2 rounded border border-neutral-300 hover:bg-neutral-50"
                type="button"
                onClick={addPage}
              >
                + Add page
              </button>
            </div>

            {/* Writing tips */}
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
              <p className="text-sm font-medium text-blue-900 mb-2">Tips for great illustrations:</p>
              <ul className="text-xs text-blue-800 space-y-1 ml-4 list-disc">
                <li><strong>Be specific:</strong> "A curious girl with curly brown hair examines a butterfly" works better than "A student looking at something"</li>
                <li><strong>Set the scene:</strong> Include details like "sunny classroom", "autumn playground", or "cozy reading corner"</li>
                <li><strong>Show emotions:</strong> Describe expressions like "eyes wide with wonder" or "proud smile"</li>
                <li><strong>Keep characters consistent:</strong> Use the same descriptions for recurring characters across pages</li>
              </ul>
            </div>
            {pagesLoading ? (
              <p className="mt-4 text-sm text-neutral-500">Loading pages…</p>
            ) : (
              <div className="space-y-4">
                {pages.map((page, index) => {
                  const statusValue = (page?.jobStatus || "").toLowerCase();
                  const normalizedStatus = (
                    statusValue || (page?.illustration_ready ? "ready" : page?.last_error ? "error" : "pending")
                  ) as PageStatusKey;
                  const hasText = (page.text || "").trim().length > 0;
                  return (
                    <div key={`${page.id || "new"}-${page.page_no}-${index}`} className={`rounded-lg border-2 bg-white p-4 shadow-sm transition-colors ${hasText ? "border-emerald-200" : "border-neutral-200"}`}>
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100 font-semibold text-sm">{page.page_no}</span>
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              PAGE_STATUS_CLASSES[normalizedStatus] || "bg-neutral-100 text-neutral-600"
                            }`}
                          >
                            {PAGE_STATUS_LABELS[normalizedStatus] || "Pending"}
                          </span>
                          {hasText && <span className="text-xs text-emerald-600">Ready for illustration</span>}
                        </div>
                        <button
                          type="button"
                          className="text-sm text-rose-600 hover:underline"
                          onClick={() => removePage(index)}
                          disabled={pages.length <= 1}
                          title={pages.length <= 1 ? "At least one page is required" : "Remove page"}
                        >
                          Remove
                        </button>
                      </div>
                      <textarea
                        className="w-full min-h-[120px] resize-y rounded-lg border border-neutral-300 p-3 focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                        placeholder={`Page ${page.page_no}: Describe what happens in this scene...\n\nExample: "Two children sit cross-legged on a colorful rug, listening intently as their teacher holds up a picture book. Warm afternoon light streams through the classroom windows. The children's faces show curiosity and delight."`}
                        value={page.text}
                        onChange={(event) => updatePageText(index, event.target.value)}
                      />
                      {page.image_url && (
                        <div className="mt-2 text-xs text-neutral-500">
                          Illustration: <code className="break-all bg-neutral-100 px-1 rounded">{page.image_url}</code>
                        </div>
                      )}
                      {page.last_error && (
                        <div className="mt-2 text-xs text-rose-600">Error: {page.last_error}</div>
                      )}
                    </div>
                  );
                })}
                {!pages.length && (
                  <div className="rounded-lg border-2 border-dashed border-neutral-300 bg-neutral-50 p-8 text-center">
                    <p className="text-neutral-600 mb-3">No pages yet. Add your first page to get started.</p>
                    <button
                      type="button"
                      className="px-4 py-2 rounded bg-amber-700 text-white hover:bg-amber-800"
                      onClick={addPage}
                    >
                      + Add first page
                    </button>
                  </div>
                )}
              </div>
            )}
            {pages.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className="px-4 py-2 rounded border border-neutral-300 hover:bg-neutral-50 disabled:opacity-60"
                  onClick={onSavePages}
                  disabled={savingPages || !pagesDirty}
                >
                  {savingPages ? "Saving…" : "Save pages"}
                </button>
                {pagesDirty && <span className="text-xs text-amber-700">Unsaved changes</span>}
                <span className="text-xs text-neutral-500">{pages.filter((p) => (p.text || "").trim()).length} of {pages.length} pages have descriptions</span>
              </div>
            )}
          </section>

          {/* Step 2: Generate Illustrations - PROMINENT */}
          <section className="mb-8 rounded-xl border-2 border-amber-300 bg-amber-50 p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-amber-900">Step 2: Generate Illustrations</h3>
                <p className="text-sm text-amber-800 mt-1">
                  Once all pages have descriptions, click to generate consistent illustrations across your storybook.
                </p>
                {!pagesReady && pages.length > 0 && (
                  <p className="text-sm text-amber-700 mt-2 font-medium">
                    {pages.filter((p) => !(p.text || "").trim()).length} page(s) still need descriptions before you can generate.
                  </p>
                )}
              </div>
              <button
                className="px-6 py-3 rounded-lg bg-amber-700 text-white font-semibold hover:bg-amber-800 disabled:opacity-60 disabled:cursor-not-allowed shadow-md"
                onClick={onGenerateIllustrations}
                disabled={busy || !pagesReady}
                title={!pages.length ? "Add storyboard pages first" : !pagesReady ? "All pages need descriptions" : ""}
              >
                {busy ? "Generating…" : "Generate All Illustrations"}
              </button>
            </div>
            {jobStatus && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-white px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {jobStatus.status === "running" && (
                      <span className="inline-block h-3 w-3 rounded-full bg-blue-500 animate-pulse"></span>
                    )}
                    <span className="font-medium text-sm">
                      {jobStatus.status === "running" ? "Generating illustrations…" :
                       jobStatus.status === "complete" ? "Complete!" :
                       jobStatus.status === "complete-with-errors" ? "Completed with some errors" :
                       jobStatus.status === "failed" ? "Generation failed" :
                       "Queued"}
                    </span>
                  </div>
                  <span className="text-sm text-neutral-600">
                    {Number(jobStatus.completed_pages ?? 0)} / {Number(jobStatus.total_pages ?? pages.length)} pages
                  </span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-neutral-200 overflow-hidden">
                  <div
                    className="h-full bg-amber-600 transition-all duration-500"
                    style={{ width: `${Math.round((Number(jobStatus.completed_pages ?? 0) / Math.max(Number(jobStatus.total_pages ?? pages.length), 1)) * 100)}%` }}
                  ></div>
                </div>
              </div>
            )}
            {pollError && <p className="mt-2 text-sm text-rose-600">{pollError}</p>}
          </section>

          {/* Step 3: Export PDF */}
          <section className="mb-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold">Step 3: Export & Share</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  Generate a printable PDF of your storybook to share with students and families.
                </p>
              </div>
              <button
                className="px-4 py-2 rounded bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60"
                onClick={onExportPdf}
                disabled={busy || !illustrationsComplete}
                title={!illustrationsComplete ? "Generate illustrations first" : ""}
              >
                {busy ? "Working…" : "Export PDF"}
              </button>
            </div>

            {pdfUrl && (
              <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-sm font-medium text-emerald-800 mb-2">Your PDF is ready!</p>
                <a className="text-emerald-700 underline break-all text-sm" href={pdfUrl} target="_blank" rel="noreferrer">
                  {pdfUrl}
                </a>
              </div>
            )}
          </section>

          {/* Optional: AI Text Generation (collapsed by default) */}
          <details className="mb-8 rounded-lg border border-neutral-200 bg-neutral-50">
            <summary className="cursor-pointer px-4 py-3 font-medium text-neutral-700 hover:bg-neutral-100">
              Optional: Generate story text with AI
            </summary>
            <div className="px-4 pb-4 pt-2 border-t border-neutral-200">
              <p className="text-sm text-neutral-600 mb-3">
                Use AI to generate narrative text based on your notes. You can then sync this to your pages or use it as inspiration.
              </p>
              <label className="block text-sm font-medium mb-2">Teacher notes</label>
              <textarea
                className="w-full min-h-[100px] p-3 border rounded-lg"
                placeholder="Share themes, events, vocabulary, or character details you want included…"
                value={teacherNotes}
                onChange={(e) => setTeacherNotes(e.target.value)}
              />
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  className="px-4 py-2 rounded border border-neutral-300 hover:bg-white disabled:opacity-60"
                  onClick={onGenerateText}
                  disabled={busy}
                >
                  {busy ? "Working…" : "Generate narrative"}
                </button>
                {narrative && (
                  <button
                    className="px-4 py-2 rounded border border-amber-300 text-amber-700 hover:bg-amber-50"
                    type="button"
                    onClick={syncPagesFromNarrative}
                  >
                    Sync to pages
                  </button>
                )}
              </div>
              {narrative && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-2">Generated narrative</label>
                  <textarea
                    readOnly
                    className="w-full min-h-[150px] p-3 border rounded-lg bg-white text-sm"
                    value={narrative}
                  />
                </div>
              )}
            </div>
          </details>

          <div className="flex items-center gap-3">
            <button className="px-4 py-2 rounded border" onClick={() => navigate(-1)}>
              Back
            </button>
            <button
              className="ml-auto px-4 py-2 rounded bg-amber-700 text-white"
              onClick={() => navigate("/portal/teacher/storybooks")}
            >
              Done
            </button>
          </div>
        </>
      )}
    </div>
  );
}
